import { DEFAULT_MODEL_FOR_ASSESSMENT_QUERY } from "../components/AISettings";

import { AIAssessmentEvaluationDataType, serverPostAssessmentQuery } from "api/api-server-ai";
import { AIModelType, AIServerMessageType, SubmissionType } from "components/server-types";
import { t } from "i18n/i18n";
import { store } from "store/index";
import { selectCurrentSectionId, selectNestedPageOutcomes } from "store/selectors";
import { rollbarAndLogError } from "utils/logger";

export type AIAssessmentResponseType = {
  outcomes: { pageOutcomeId: string; score: number }[];
  feedback: string;
  refused?: string;
  errorMessage?: string;
};

export type AIAssessmentEvaluationResponseType = AIAssessmentResponseType & {
  evaluationData?: AIAssessmentEvaluationDataType;
};

type AssessmentRequestMode = "assessment" | "evaluation";

const requestAssessment = async (
  submission: Pick<SubmissionType, "id" | "page_id">,
  model: AIModelType,
  requestMode: AssessmentRequestMode,
): Promise<AIAssessmentEvaluationResponseType> => {
  try {
    const state = store.getState();
    const sectionId = selectCurrentSectionId(state);
    const pageId = submission.page_id;
    const pageOutcomes = selectNestedPageOutcomes(state, pageId);

    const prompt: AIServerMessageType[] = [
      {
        type: "user",
        role: "user",
        content: "Please help me with assessing the current student submission.",
      },
    ];
    const { result: assessmentResponse, evaluation_data: evaluationData } =
      await serverPostAssessmentQuery({
        section_id: sectionId,
        submission_id: submission.id,
        model,
        prompt,
        ...(requestMode === "evaluation" ? { include_evaluation_data: true } : {}),
      });

    if (assessmentResponse.refused) {
      return {
        outcomes: [],
        feedback: "",
        refused: t("assessment_card.ai.refused", { reason: assessmentResponse.refused }),
        ...(evaluationData ? { evaluationData } : {}),
      };
    }

    const outcomes = Object.entries(assessmentResponse.outcomes)
      .map(([outcomeName, score]) => {
        const outcomeTitle = outcomeName.replaceAll("#", "");
        const pageOutcome = pageOutcomes.find(
          (candidatePageOutcome) => candidatePageOutcome.outcome.title === outcomeTitle,
        );
        if (!pageOutcome) {
          return null;
        }
        return {
          pageOutcomeId: pageOutcome.id,
          score: Number(score),
        };
      })
      .filter(
        (mappedOutcome): mappedOutcome is { pageOutcomeId: string; score: number } =>
          mappedOutcome != null,
      );

    if (pageOutcomes.length > 0 && outcomes.length < pageOutcomes.length) {
      rollbarAndLogError("serverAssessSubmission: partial outcome mapping.", {
        expected: pageOutcomes.length,
        received: outcomes.length,
      });
      return {
        outcomes: [],
        feedback: "",
        errorMessage: t("assessment_card.ai.error"),
        ...(evaluationData ? { evaluationData } : {}),
      };
    }

    return {
      outcomes,
      feedback: (assessmentResponse.feedback ?? "").trim(),
      ...(evaluationData ? { evaluationData } : {}),
    };
  } catch {
    // serverPostAssessmentQuery already reports via withApiErrorReporting.
    return {
      outcomes: [],
      feedback: "",
      errorMessage: t("assessment_card.ai.error"),
    };
  }
};

export const serverAssessSubmission = async (
  submission: Pick<SubmissionType, "id" | "page_id">,
  model: AIModelType = DEFAULT_MODEL_FOR_ASSESSMENT_QUERY,
): Promise<AIAssessmentResponseType> => requestAssessment(submission, model, "assessment");

export const serverEvaluateSubmission = async (
  submission: Pick<SubmissionType, "id" | "page_id">,
  model: AIModelType = DEFAULT_MODEL_FOR_ASSESSMENT_QUERY,
): Promise<AIAssessmentEvaluationResponseType> => {
  const evaluationResponse = await requestAssessment(submission, model, "evaluation");
  if (!evaluationResponse.errorMessage && !evaluationResponse.evaluationData) {
    rollbarAndLogError("serverEvaluateSubmission: missing evaluation data.", {
      submissionId: submission.id,
    });
    return {
      outcomes: [],
      feedback: "",
      errorMessage: t("assessment_card.ai.error"),
    };
  }
  return evaluationResponse;
};
